import _ from 'lodash';
import type { ObjectDefinition } from '../../types/Schema';
import { findObjectIndexInList } from '../../modules/studio/utils-ts';
import type { StudioRootObjectsActions } from '../../actions/studio/rootObjects';
import type { RootFieldModel } from '../../types/FieldModel';
import type { StudioProjectActions } from '../../actions/studio';
import { RESET_STUDIO_ACTION } from '../consts';
import type AppError from '../../lib/AppError';

interface DeleteObjectValidationErrors {
    validations: Array<
        ObjectDefinition & {
            type: string;
        }
    >;
    failure?: AppError;
}

export type CreateObject = {
    id: string;
    url?: string;
    model: RootFieldModel;
};
export type DeleteObject = ObjectDefinition & {
    isRequestPending: boolean;
    isDeleting: boolean;
    isDeleted: boolean;
    errors: DeleteObjectValidationErrors | null;
};

export interface State {
    creating: CreateObject[];
    deleting: DeleteObject[];
    updatingSlug: {
        for: ObjectDefinition;
    } | null;
}

const getDefaultState = (): State => ({
    creating: [],
    deleting: [],
    updatingSlug: null
});

const studioPageReducer = (state = getDefaultState(), action: StudioRootObjectsActions | StudioProjectActions | null): State => {
    switch (action?.type) {
        case 'CREATE_OBJECT_SUCCEED':
        case 'CREATE_OBJECT_FAILED': {
            return {
                ...state,
                creating: state.creating.filter((o) => o.id !== action.data.id)
            };
        }

        case 'CREATE_OBJECT_STARTED': {
            const creating = [...state.creating];
            creating.push({
                id: action.data.id,
                url: action.data.url,
                model: action.data.model
            });
            return {
                ...state,
                creating
            };
        }

        case 'CREATE_OBJECT_UPDATE_URL': {
            const creating = [...state.creating];
            const creatingObjectIndex = creating.findIndex((o) => o.id === action.data.id);
            if (creatingObjectIndex >= 0) {
                creating[creatingObjectIndex] = {
                    ...creating[creatingObjectIndex]!,
                    url: action.data.url
                };
            }
            return {
                ...state,
                creating
            };
        }

        case 'DELETE_OBJECT_FAILED': {
            const { errors } = action.data || {};
            const update = [...state.deleting];
            const index = findObjectIndexInList(update, action.data);
            const pageBeingDeleted = update[index];
            if (index >= 0) {
                update.splice(index, 1);
            }
            if (!_.isEmpty(errors) && pageBeingDeleted) {
                update.splice(index, 0, {
                    ...pageBeingDeleted,
                    isDeleting: false,
                    isRequestPending: false,
                    errors
                });
            }

            return {
                ...state,
                deleting: update
            };
        }

        case 'DELETE_OBJECT_STARTED': {
            const { srcObjectId, srcProjectId, srcType } = action.data || {};

            const update = [...state.deleting];
            const index = findObjectIndexInList(update, action.data);
            const object = {
                srcObjectId,
                srcProjectId,
                srcType,
                errors: null,
                isDeleting: true,
                isRequestPending: true,
                isDeleted: false
            };

            if (index >= 0) {
                update[index] = object;
            } else {
                update.push(object);
            }

            return {
                ...state,
                deleting: update
            };
        }

        case 'DELETE_OBJECT_REQUEST_SUCCESS': {
            const update = [...state.deleting];
            const index = findObjectIndexInList(update, action.data);
            const object = update[index];

            if (object) {
                update[index] = {
                    ...object,
                    isRequestPending: false
                };
            }

            return {
                ...state,
                deleting: update
            };
        }

        case 'DELETE_OBJECT_SUCCEEDED': {
            const update = [...state.deleting];
            const index = findObjectIndexInList(update, action.data);
            const object = update[index];

            if (object) {
                update[index] = {
                    ...object,
                    isDeleting: false,
                    isDeleted: true
                };
            }

            return {
                ...state,
                deleting: update
            };
        }

        case 'DELETE_OBJECT_DISMISS_ERROR': {
            const objectsBeingDeleted = state.deleting.filter((object) => !object.errors);
            return {
                ...state,
                deleting: objectsBeingDeleted
            };
        }

        case 'FETCH_SITE_MAP_SUCCEEDED': {
            // on sitemap update, update created && deleted
            const { sitemapFlatten } = action.data || {};
            const { creating: objectsBeingCreated, deleting: objectsBeingDeleted } = state;
            const newState = { ...state };

            sitemapFlatten.forEach((entry) => {
                // If a page is currently being created, we must check whether its
                // URL is part of the updated sitemap. If it is, it means that the
                // page has been created and the new state must reflect it.

                const pageCreatingIndex = objectsBeingCreated.findIndex(
                    (o) =>
                        o.model.srcType === entry.srcType &&
                        o.model.srcProjectId === entry.srcProjectId &&
                        o.model.name === entry.srcModelName &&
                        o.url === entry.url
                );

                if (pageCreatingIndex >= 0) {
                    // Creating a copy of `pagesBeingDeleted` that includes all its
                    // previous members except for the `entry.url` key.
                    newState.creating = [...objectsBeingCreated];
                    newState.creating.splice(pageCreatingIndex, 1);
                }

                // If a page is currently being deleted, we must check whether it's
                // absent from the updated sitemap. If it is, it means that the
                // page has been deleted and the new state must reflect it.
                const pageDeleteIndex = objectsBeingDeleted.findIndex(
                    (page) => page.srcObjectId === entry.objectId && page.srcProjectId === entry.srcProjectId && page.srcType === entry.srcType
                );

                if (pageDeleteIndex >= 0 && entry.objectId) {
                    // Creating a copy of `pagesBeingDeleted` that includes all its
                    // previous members except for the `entry.objectId` key.
                    newState.deleting = [...state.deleting];
                    newState.deleting.splice(pageDeleteIndex, 1);
                }
            });

            return newState;
        }

        case 'SET_OBJECT_UPDATING_SLUG': {
            return {
                ...state,
                updatingSlug: action.data
            };
        }

        case RESET_STUDIO_ACTION: {
            return getDefaultState();
        }

        default: {
            return state;
        }
    }
};

export default studioPageReducer;
